(() => {
  'use strict';

  const theme = window.RoadSight || {};
  const qs = (selector, scope = document) => scope.querySelector(selector);
  const qsa = (selector, scope = document) => [...scope.querySelectorAll(selector)];
  const escapeHTML = (value = '') => String(value).replace(/[&<>'"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[char]));
  const debounce = (fn, delay = 250) => {
    let timer;
    return (...args) => { clearTimeout(timer); timer = setTimeout(() => fn(...args), delay); };
  };

  function formatMoney(cents, format = theme.moneyFormat || '${{amount}}') {
    const amount = Number(cents || 0) / 100;
    const token = (format.match(/\{\{\s*(\w+)\s*\}\}/) || [null, 'amount'])[1];
    const options = token.includes('no_decimals')
      ? { minimumFractionDigits: 0, maximumFractionDigits: 0 }
      : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
    let value = amount.toLocaleString(undefined, options);
    if (token.includes('with_comma_separator')) value = value.replace(/,/g, '_').replace(/\./g, ',').replace(/_/g, '.');
    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  function formatMajorMoney(amount) {
    return formatMoney(Math.round(Number(amount || 0) * 100));
  }

  function resizedImageUrl(value, width) {
    if (!value) return '';
    try {
      const url = new URL(value, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (_) { return value; }
  }

  function showToast(message) {
    const toast = qs('[data-toast]');
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('is-visible');
    clearTimeout(showToast.timer);
    showToast.timer = setTimeout(() => toast.classList.remove('is-visible'), 2600);
  }

  const overlay = qs('[data-drawer-overlay]');
  let activeDrawer = null;
  let returnFocus = null;

  function openDrawer(drawer, opener = document.activeElement) {
    if (!drawer) return;
    if (activeDrawer && activeDrawer !== drawer) closeDrawer(activeDrawer, false);
    returnFocus = opener;
    activeDrawer = drawer;
    drawer.removeAttribute('inert');
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    drawer.closest('[data-site-header]')?.classList.add('site-header--drawer-open');
    if (opener instanceof HTMLElement) opener.setAttribute('aria-expanded', 'true');
    document.body.classList.add('is-locked');
    if (overlay) { overlay.hidden = false; requestAnimationFrame(() => overlay.classList.add('is-open')); }
    const focusable = qs('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])', drawer);
    if (focusable) setTimeout(() => focusable.focus(), 80);
  }

  function closeDrawer(drawer = activeDrawer, restore = true) {
    if (!drawer) return;
    const opener = returnFocus;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    drawer.closest('[data-site-header]')?.classList.remove('site-header--drawer-open');
    if (drawer.matches('[data-filter-drawer]') && window.matchMedia('(min-width: 990px)').matches) {
      drawer.removeAttribute('inert');
      drawer.setAttribute('aria-hidden', 'false');
    } else drawer.setAttribute('inert', '');
    if (opener instanceof HTMLElement) opener.setAttribute('aria-expanded', 'false');
    activeDrawer = null;
    document.body.classList.remove('is-locked');
    if (overlay) { overlay.classList.remove('is-open'); setTimeout(() => { if (!activeDrawer) overlay.hidden = true; }, 250); }
    if (restore && opener instanceof HTMLElement) opener.focus();
    returnFocus = null;
  }

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeDrawer();
    }
    if (event.key !== 'Tab' || !activeDrawer) return;
    const focusable = qsa('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])', activeDrawer).filter((el) => el.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });

  function openSearch(opener) {
    const panel = qs('[data-search-panel]');
    if (!panel) return;
    openDrawer(panel, opener);
  }
  function closeSearch() {
    const panel = qs('[data-search-panel]');
    if (!panel) return;
    closeDrawer(panel);
  }

  document.addEventListener('click', (event) => {
    const target = event.target;
    const mobileOpener = target.closest('[data-mobile-menu-open]');
    const cartOpener = target.closest('[data-cart-open]');
    const filterOpener = target.closest('[data-filter-open]');
    const searchOpener = target.closest('[data-search-open]');
    if (mobileOpener) openDrawer(qs('[data-mobile-menu]'), mobileOpener);
    if (target.closest('[data-mobile-menu-close]')) closeDrawer();
    if (cartOpener) openCart(cartOpener);
    if (target.closest('[data-drawer-close]')) closeDrawer();
    if (filterOpener) openDrawer(qs('[data-filter-drawer]'), filterOpener);
    if (target.closest('[data-filter-close]')) closeDrawer();
    if (searchOpener) openSearch(searchOpener);
    if (target.closest('[data-search-close]')) closeSearch();
    if (target.matches('[data-drawer-overlay]')) closeDrawer();
  });

  async function getCart() {
    const response = await fetch(theme.routes.cart, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Unable to load the cart.');
    return response.json();
  }

  function cartItemMarkup(item) {
    const image = item.image ? `<img src="${escapeHTML(resizedImageUrl(item.image, 180))}" width="90" height="90" loading="lazy" alt="${escapeHTML(item.product_title)}">` : '';
    const variant = item.variant_title && item.variant_title !== 'Default Title' ? `<small>${escapeHTML(item.variant_title)}</small>` : '';
    return `<div class="cart-item" data-cart-key="${escapeHTML(item.key)}">
      <a class="cart-item__image" href="${escapeHTML(item.url)}">${image}</a>
      <div class="cart-item__details">
        <a class="cart-item__title" href="${escapeHTML(item.url)}">${escapeHTML(item.product_title)}</a>
        ${variant}<span>${formatMoney(item.final_line_price)}</span>
        <div class="quantity-selector quantity-selector--small">
          <button type="button" data-cart-quantity="${Math.max(0, item.quantity - 1)}" aria-label="Decrease quantity"><svg aria-hidden="true" viewBox="0 0 24 24"><path d="M5 12h14"></path></svg></button>
          <span>${item.quantity}</span>
          <button type="button" data-cart-quantity="${item.quantity + 1}" aria-label="Increase quantity"><svg aria-hidden="true" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14"></path></svg></button>
        </div>
        <button class="text-link" type="button" data-cart-remove>Remove</button>
      </div>
    </div>`;
  }

  function renderCart(cart) {
    const drawer = qs('[data-cart-drawer]');
    if (!drawer) return;
    const body = qs('[data-cart-drawer-body]', drawer);
    const count = cart.item_count;
    qsa('[data-cart-count]').forEach((el) => { el.textContent = count; el.classList.toggle('is-empty', count === 0); });
    qsa('[data-cart-open]').forEach((el) => el.setAttribute('aria-label', `Cart, ${count} items`));
    const headingCount = qs('[data-cart-count-heading]', drawer);
    if (headingCount) headingCount.textContent = count ? `(${count})` : '';
    if (!count) {
      body.innerHTML = `<div class="cart-drawer__empty"><svg aria-hidden="true" viewBox="0 0 24 24"><path d="M3 4h2l2.1 10.2a2 2 0 0 0 2 1.6h7.8a2 2 0 0 0 2-1.6L20.4 7H6"></path><circle cx="9" cy="20" r="1"></circle><circle cx="17" cy="20" r="1"></circle></svg><p>Your cart is ready for the road.</p><a class="button" href="${escapeHTML(theme.routes.allProducts || '/collections/all')}">Continue shopping</a></div>`;
      qs('[data-cart-drawer-footer]', drawer)?.remove();
      return;
    }
    body.innerHTML = `<div class="cart-drawer__items">${cart.items.map(cartItemMarkup).join('')}</div>`;
    let footer = qs('[data-cart-drawer-footer]', drawer);
    if (!footer) { footer = document.createElement('div'); footer.className = 'cart-drawer__footer'; footer.dataset.cartDrawerFooter = ''; drawer.append(footer); }
    const threshold = Number(theme.freeShippingThreshold || 0);
    const remaining = threshold - cart.total_price;
    const progress = threshold > 0 ? `<div class="shipping-progress"><p>${remaining > 0 ? `You're ${formatMoney(remaining)} away from free shipping.` : 'Free shipping unlocked.'}</p><div class="shipping-progress__track"><span style="width:${Math.min(100, cart.total_price * 100 / threshold)}%"></span></div></div>` : '';
    footer.innerHTML = `${progress}<div class="cart-drawer__subtotal"><span>Subtotal</span><strong>${formatMoney(cart.total_price)}</strong></div><p class="cart-drawer__tax-note">Taxes and shipping calculated at checkout.</p><form action="${escapeHTML(theme.routes.cartPage || '/cart')}" method="post"><button class="button button--full" type="submit" name="checkout">Checkout</button></form>`;
  }

  async function refreshCart(open = false) {
    const cart = await getCart();
    renderCart(cart);
    if (open) openDrawer(qs('[data-cart-drawer]'));
    return cart;
  }

  async function openCart(opener) {
    try { await refreshCart(false); } catch (error) { showToast(error.message); }
    openDrawer(qs('[data-cart-drawer]'), opener);
  }

  document.addEventListener('click', async (event) => {
    const quantityButton = event.target.closest('[data-cart-quantity], [data-cart-remove]');
    if (!quantityButton) return;
    const item = quantityButton.closest('[data-cart-key]');
    if (!item) return;
    const quantity = quantityButton.hasAttribute('data-cart-remove') ? 0 : Number(quantityButton.dataset.cartQuantity);
    quantityButton.disabled = true;
    try {
      const response = await fetch(theme.routes.cartChange, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: item.dataset.cartKey, quantity }) });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || 'Unable to update the cart.');
      renderCart(cart);
    } catch (error) { showToast(error.message); quantityButton.disabled = false; }
  });

  async function addFormToCart(form) {
    const submit = qs('[type="submit"]', form);
    const original = submit?.innerHTML;
    if (submit) { submit.disabled = true; submit.setAttribute('aria-busy', 'true'); }
    let added = false;
    try {
      const response = await fetch(theme.routes.cartAdd, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'Unable to add this item.');
      added = true;
      showToast(theme.strings?.added || 'Added to cart');
      try { await refreshCart(Boolean(theme.cartDrawerEnabled)); }
      catch (_) { showToast('Item added. Open the cart to review it.'); }
    } catch (error) {
      if (added) return;
      const errorBox = qs('[data-product-form-error]', form);
      if (errorBox) { errorBox.hidden = false; errorBox.textContent = error.message; }
      else showToast(error.message);
    } finally {
      if (submit) { submit.disabled = false; submit.removeAttribute('aria-busy'); submit.innerHTML = original; }
    }
  }

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-product-form], [data-quick-add]');
    if (!form) return;
    event.preventDefault();
    addFormToCart(form);
  });

  function initQuantity(scope = document) {
    qsa('[data-quantity-selector]', scope).forEach((selector) => {
      if (selector.dataset.initialized) return;
      selector.dataset.initialized = 'true';
      const input = qs('input[type="number"]', selector);
      qs('[data-quantity-minus]', selector)?.addEventListener('click', () => { input.stepDown(); input.dispatchEvent(new Event('change', { bubbles: true })); });
      qs('[data-quantity-plus]', selector)?.addEventListener('click', () => { input.stepUp(); input.dispatchEvent(new Event('change', { bubbles: true })); });
    });
  }

  function initImageComparison(scope = document) {
    qsa('[data-image-comparison]', scope).forEach((section) => {
      if (section.dataset.initialized) return;
      section.dataset.initialized = 'true';
      const slider = qs('.comparison-slider', section);
      const range = qs('.comparison-slider__range', section);
      range?.addEventListener('input', () => slider.style.setProperty('--comparison-position', `${range.value}%`));
    });
  }

  function initProduct(scope = document) {
    qsa('[data-product-section]', scope).forEach((section) => {
      if (section.dataset.initialized) return;
      section.dataset.initialized = 'true';
      const json = qs('[data-product-json]', section);
      if (!json) return;
      const product = JSON.parse(json.textContent);
      const commerceJson = qs('[data-product-commerce-json]', section);
      const commerce = commerceJson ? JSON.parse(commerceJson.textContent) : { requiresSellingPlan: false, variants: {} };
      let currentVariant = product.variants.find((variant) => variant.id === Number(qs('[data-variant-id]', section)?.value)) || product.variants[0];

      const selectVariant = () => {
        const options = qsa('.variant-option', section).map((option) => qs('input:checked', option)?.value);
        const variant = product.variants.find((candidate) => candidate.options.every((value, index) => value === options[index]));
        currentVariant = variant;
        const idInput = qs('[data-variant-id]', section);
        const button = qs('[data-add-to-cart]', section);
        const buttonText = qs('[data-add-to-cart-text]', section);
        const buttonPrice = qs('[data-add-to-cart-price]', section);
        if (!variant) {
          if (button) button.disabled = true;
          if (buttonText) buttonText.textContent = theme.strings?.unavailable || 'Unavailable';
          return;
        }
        if (idInput) idInput.value = variant.id;
        qsa('[data-selected-option]', section).forEach((label, index) => { label.textContent = options[index] || ''; });
        const commerceVariant = commerce.variants?.[String(variant.id)] || {};
        const quantityRule = commerceVariant.quantityRule || { min: 1, max: null, increment: 1 };
        const quantityInput = qs('[data-quantity-input]', section);
        if (quantityInput) {
          quantityInput.min = quantityRule.min || 1;
          quantityInput.step = quantityRule.increment || 1;
          if (quantityRule.max) quantityInput.max = quantityRule.max; else quantityInput.removeAttribute('max');
          if (Number(quantityInput.value) < Number(quantityInput.min)) quantityInput.value = quantityInput.min;
        }
        const sellingPlan = qs('[data-selling-plan]', section);
        const plans = commerceVariant.sellingPlans || [];
        if (sellingPlan) {
          const previousPlan = sellingPlan.value;
          const oneTime = commerce.requiresSellingPlan ? '' : '<option value="">One-time purchase</option>';
          sellingPlan.innerHTML = oneTime + plans.map((plan) => `<option value="${escapeHTML(plan.id)}">${escapeHTML(plan.name)}</option>`).join('');
          if ([...sellingPlan.options].some((option) => option.value === previousPlan)) sellingPlan.value = previousPlan;
        }
        const canPurchase = variant.available && (!commerce.requiresSellingPlan || plans.length > 0);
        if (button) button.disabled = !canPurchase;
        if (buttonText) buttonText.textContent = canPurchase ? (theme.strings?.addToCart || 'Add to cart') : (variant.available ? (theme.strings?.unavailable || 'Unavailable') : (theme.strings?.soldOut || 'Sold out'));
        if (buttonPrice) buttonPrice.textContent = formatMoney(variant.price);
        const priceTarget = qs('[data-product-price]', section);
        const unitPrice = variant.unit_price_measurement ? `<small class="unit-price">${formatMoney(variant.unit_price)} / ${variant.unit_price_measurement.reference_value !== 1 ? variant.unit_price_measurement.reference_value : ''}${escapeHTML(variant.unit_price_measurement.reference_unit)}</small>` : '';
        if (priceTarget) priceTarget.innerHTML = `<div class="price${variant.compare_at_price > variant.price ? ' price--on-sale' : ''}"><span class="price__current">${formatMoney(variant.price)}</span>${variant.compare_at_price > variant.price ? `<s class="price__compare">${formatMoney(variant.compare_at_price)}</s>` : ''}${unitPrice}</div>`;
        const stickyPrice = qs('[data-sticky-price]'); if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
        const stickyVariant = qs('[data-sticky-variant]'); if (stickyVariant) stickyVariant.textContent = variant.title;
        const stickyButton = qs('[data-sticky-add]'); if (stickyButton) stickyButton.disabled = !canPurchase;
        if (variant.featured_media) activateMedia(section, variant.featured_media.id);
        const variantUrl = new URL(window.location.href); variantUrl.searchParams.set('variant', variant.id); history.replaceState({}, '', variantUrl);
      };

      qsa('[data-option-value]', section).forEach((input) => input.addEventListener('change', selectVariant));
      qsa('[data-media-target]', section).forEach((button) => button.addEventListener('click', () => activateMedia(section, Number(button.dataset.mediaTarget))));
      qs('[data-sticky-add]')?.addEventListener('click', () => qs('[data-product-form]', section)?.requestSubmit());
      const form = qs('[data-product-form]', section);
      const sticky = qs('[data-sticky-atc]');
      if (form && sticky && 'IntersectionObserver' in window) new IntersectionObserver(([entry]) => {
        const hidden = entry.isIntersecting;
        sticky.classList.toggle('is-visible', !hidden);
        sticky.setAttribute('aria-hidden', String(hidden));
        if (hidden) sticky.setAttribute('inert', ''); else sticky.removeAttribute('inert');
      }, { threshold: 0 }).observe(form);
    });
  }

  function activateMedia(section, id) {
    qsa('[data-media-id]', section).forEach((media) => {
      const active = Number(media.dataset.mediaId) === Number(id);
      media.classList.toggle('is-active', active);
      media.setAttribute('aria-hidden', String(!active));
      if (!active) qs('video', media)?.pause();
      const iframe = qs('iframe', media);
      if (iframe && !active && iframe.src !== 'about:blank') { iframe.dataset.src = iframe.src; iframe.src = 'about:blank'; }
      if (iframe && active && iframe.dataset.src) { iframe.src = iframe.dataset.src; delete iframe.dataset.src; }
    });
    qsa('[data-media-target]', section).forEach((button) => button.classList.toggle('is-active', Number(button.dataset.mediaTarget) === Number(id)));
  }

  function initPredictiveSearch(scope = document) {
    qsa('[data-predictive-search-input]', scope).forEach((input) => {
      if (input.dataset.initialized) return;
      input.dataset.initialized = 'true';
      const results = qs('[data-predictive-results]', input.closest('[data-search-panel]'));
      let requestVersion = 0;
      const search = debounce(async () => {
        const term = input.value.trim();
        const version = ++requestVersion;
        if (term.length < 2) { results.innerHTML = ''; input.setAttribute('aria-expanded', 'false'); return; }
        try {
          const url = `${theme.routes.search}.json?q=${encodeURIComponent(term)}&resources[type]=product,page,article&resources[limit]=6&resources[options][unavailable_products]=last`;
          const response = await fetch(url, { headers: { Accept: 'application/json' } });
          const data = await response.json();
          if (version !== requestVersion) return;
          const payload = data.resources?.results || {};
          const items = [...(payload.products || []), ...(payload.pages || []), ...(payload.articles || [])].slice(0, 6);
          results.innerHTML = items.map((item) => `<a class="predictive-result" role="option" href="${escapeHTML(item.url)}">${item.image ? `<img src="${escapeHTML(resizedImageUrl(item.image, 140))}" width="70" height="70" alt="">` : '<span></span>'}<span><strong>${escapeHTML(item.title)}</strong>${item.price ? `<small>${formatMajorMoney(item.price)}</small>` : '<small>Guide or page</small>'}</span></a>`).join('');
          input.setAttribute('aria-expanded', String(items.length > 0));
        } catch (_) { if (version === requestVersion) { results.innerHTML = ''; input.setAttribute('aria-expanded', 'false'); } }
      }, 220);
      input.addEventListener('input', search);
      input.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowDown') return;
        const first = qs('[role="option"]', results);
        if (first) { event.preventDefault(); first.focus(); }
      });
      results.addEventListener('keydown', (event) => {
        const options = qsa('[role="option"]', results);
        const index = options.indexOf(document.activeElement);
        if (event.key === 'ArrowDown' && options[index + 1]) { event.preventDefault(); options[index + 1].focus(); }
        if (event.key === 'ArrowUp') { event.preventDefault(); if (options[index - 1]) options[index - 1].focus(); else input.focus(); }
      });
    });
  }

  function initAutoSubmit(scope = document) {
    qsa('[data-sort-select]', scope).forEach((select) => {
      if (select.dataset.autoSubmitInitialized) return;
      select.dataset.autoSubmitInitialized = 'true';
      select.addEventListener('change', () => select.form?.submit());
    });
  }

  function closeMegaMenus(except = null) {
    qsa('[data-mega-details][open]').forEach((details) => {
      if (details !== except) details.open = false;
    });
  }

  function initMegaMenus(scope = document) {
    qsa('[data-mega-details]', scope).forEach((details) => {
      if (details.dataset.megaInitialized) return;
      details.dataset.megaInitialized = 'true';
      details.addEventListener('toggle', () => {
        if (details.open) closeMegaMenus(details);
      });
      details.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !details.open) return;
        event.preventDefault();
        details.open = false;
        qs('summary', details)?.focus();
      });
    });
  }

  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-mega-details]')) closeMegaMenus();
  });

  function syncFilterDrawer() {
    const filter = qs('[data-filter-drawer]');
    if (!filter) return;
    const desktop = window.matchMedia('(min-width: 990px)').matches;
    if (desktop) {
      if (activeDrawer === filter) closeDrawer(filter, false);
      filter.classList.remove('is-open');
      filter.removeAttribute('inert');
      filter.setAttribute('aria-hidden', 'false');
      qs('[data-filter-open]')?.setAttribute('aria-expanded', 'false');
    } else if (activeDrawer !== filter) {
      filter.setAttribute('inert', '');
      filter.setAttribute('aria-hidden', 'true');
    }
  }

  function initRecommendations(scope = document) {
    qsa('[data-product-recommendations]', scope).forEach(async (section) => {
      if (section.dataset.initialized || !section.dataset.url) return;
      section.dataset.initialized = 'true';
      try {
        const response = await fetch(section.dataset.url);
        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const content = qs('[data-product-recommendations]', parsed);
        if (content?.innerHTML.trim()) section.innerHTML = content.innerHTML;
      } catch (_) { /* Recommendations are optional. */ }
    });
  }

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-share-button]');
    if (!button) return;
    const url = button.dataset.url || location.href;
    try {
      if (navigator.share) await navigator.share({ title: document.title, url });
      else { await navigator.clipboard.writeText(url); showToast('Link copied'); }
    } catch (_) { /* User cancelled. */ }
  });

  function initStickyHeader() {
    const header = qs('[data-site-header]');
    if (!header || !header.classList.contains('site-header--sticky')) return;
    let lastY = window.scrollY;
    window.addEventListener('scroll', () => {
      const now = window.scrollY;
      header.classList.toggle('site-header--hidden', now > lastY && now > 180 && !activeDrawer);
      lastY = now;
    }, { passive: true });
  }

  function initialize(scope = document) {
    initQuantity(scope);
    initImageComparison(scope);
    initProduct(scope);
    initPredictiveSearch(scope);
    initRecommendations(scope);
    initAutoSubmit(scope);
    initMegaMenus(scope);
  }

  initialize();
  initStickyHeader();
  syncFilterDrawer();
  window.addEventListener('resize', debounce(syncFilterDrawer, 120));
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
